"use client";

import {useEffect, useState} from "react";
import OSMMap from 'ol/Map.js';
import View from 'ol/View.js';
import {Style, Circle, Fill, Stroke} from 'ol/style.js';
import Geolocation from 'ol/Geolocation.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import {apply} from 'ol-mapbox-style';
import {ObjectEvent} from "ol/Object";
import LayerGroup from "ol/layer/Group";
import {Select} from "ol/interaction";
import {SelectEvent} from "ol/interaction/Select";
import {getStepsForUserQuestState, UserQuestState} from "../services/quest.ts";
import {assert, fromTypedLonLat} from "../utils.ts";
import {Control} from "ol/control";
import {fromLonLat, getPointResolution} from "ol/proj";
import * as CircleGeometry from 'ol/geom/Circle';

import "ol/ol.css";
import "./Map.css";

const ARE_ACTIVATION_FEATURES_VISIBLE = true;

const selectSingleClick = new Select();

selectSingleClick.on('select', function (e: SelectEvent) {
    console.log(e.target.getFeatures());
});

function createQuestLocations(map: OSMMap, userQuestState: UserQuestState[]) {
    let vectorLayer = map.getLayers().getArray().find(
        (layer) => layer.get('id') === 'quest-layer'
    ) as VectorLayer<VectorSource> | undefined;

    let vectorSource: VectorSource;

    if (!vectorLayer) {
        map.addInteraction(selectSingleClick);

        vectorSource = new VectorSource();
        vectorLayer = new VectorLayer({
            source: vectorSource,
        });

        // Tag the layer so we can find it next time
        vectorLayer.set('id', 'quest-layer');
        map.addLayer(vectorLayer);
    } else {
        // Layer exists on this map, just grab its source and clear it
        vectorSource = vectorLayer.getSource()!;
        vectorSource.clear();
    }

    const steps = getStepsForUserQuestState(userQuestState);
    const features = steps.flatMap((step) => {
        return step.locations.flatMap((location) => {
            const stepFeature = new Feature();
            stepFeature.setStyle(
                new Style({
                    image: new Circle({
                        radius: 6,
                        fill: new Fill({color: 'yellow'}),
                        stroke: new Stroke({color: 'white', width: 1}),
                    }),
                })
            );

            stepFeature.setGeometry(new Point(fromLonLat(fromTypedLonLat(location.coordinate))));

            const activationFeature = new Feature();
            if (ARE_ACTIVATION_FEATURES_VISIBLE) {
                activationFeature.setStyle(
                    new Style({
                        fill: new Fill({color: 'rgb(204 150 51 / 0.2)'}),
                        stroke: new Stroke({color: 'rgb(204 138 51 / 0.5)', width: 1}),
                    })
                );
            }

            activationFeature.set("isActivationFeature", true);
            activationFeature.set("activationCondition", location.activationCondition);
            activationFeature.set("scenarioId", location.scenario.id);

            const radiusInMeters = location.activationRadius;

            const centerProjected = fromLonLat(fromTypedLonLat(location.coordinate));

            const resolution = getPointResolution('EPSG:3857', 1, centerProjected);

            const radiusInMapUnits = radiusInMeters / resolution;

            const circleGeometry = new CircleGeometry.default(centerProjected, radiusInMapUnits);
            activationFeature.setGeometry(circleGeometry);

            return [stepFeature, activationFeature];
        });
    });

    vectorSource.addFeatures(features);
    vectorSource.changed();
}

function createLocation(map: OSMMap) {
    const positionFeature = new Feature();
    positionFeature.setStyle(
        new Style({
            image: new Circle({
                radius: 6,
                fill: new Fill({color: '#3399CC'}),
                stroke: new Stroke({color: '#fff', width: 2}),
            }),
        })
    );

    const accuracyFeature = new Feature();
    accuracyFeature.setStyle(
        new Style({
            fill: new Fill({color: 'rgba(51, 153, 204, 0.2)'}),
            stroke: new Stroke({color: 'rgba(51, 153, 204, 0.5)', width: 1}),
        })
    );

    const vectorSource = new VectorSource({
        features: [accuracyFeature, positionFeature],
    });

    const vectorLayer = new VectorLayer({
        source: vectorSource,
    });

    map.addLayer(vectorLayer);

    const geolocation = new Geolocation({
        trackingOptions: {
            enableHighAccuracy: true,
        },
        projection: map.getView().getProjection(),
    });

    geolocation.on('change:accuracyGeometry', function () {
        accuracyFeature.setGeometry(geolocation.getAccuracyGeometry() ?? undefined);
    });

    let isFirstHit = true;

    geolocation.on('change:position', function () {
        const coordinates = geolocation.getPosition();

        positionFeature.setGeometry(coordinates ? new Point(coordinates) : undefined);

        if (coordinates && isFirstHit) {
            map.getView().animate({
                center: coordinates,
                zoom: 16,
                duration: 1000,
            });

            isFirstHit = false;
        }
    });

    geolocation.on('error', function (error) {
        alert('Geolocation error: ' + error.message);
    });

    const button = document.createElement('button');
    button.innerHTML = createLocationButton();
    button.className = 'svg-button';
    button.title = 'Показать мое местоположение';

    button.addEventListener('click', () => goToLocation(geolocation, map));
    button.addEventListener('dblclick', () => button.classList.toggle("off"));

    const element = document.createElement('div');
    element.className = 'ol-locate ol-unselectable';
    element.appendChild(button);

    // Добавляем кнопку на карту
    map.addControl(new Control({element: element}));

    return geolocation;
}

function createLocationButton() {
    return `
    <svg width="48" height="50" viewBox="0 0 48 50" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0 18C0 9.16344 7.16344 2 16 2H38C42.4183 2 46 5.58172 46 10V34C46 42.8366 38.8366 50 30 50H16C7.16344 50 0 42.8366 0 34V18Z" fill="#E1E1E1"/>
<path d="M22.9998 14C17.8525 14 13.6797 18.1727 13.6797 23.3202C13.6797 28.4676 20.0313 38 22.9998 38C25.9684 38 32.32 28.4676 32.32 23.3202C32.32 18.1727 28.1473 14 22.9998 14ZM22.9998 27.5921C20.2902 27.5921 18.0936 25.3955 18.0936 22.6858C18.0936 19.9762 20.2902 17.7796 22.9998 17.7796C25.7095 17.7796 27.906 19.9762 27.906 22.6858C27.906 25.3955 25.7095 27.5921 22.9998 27.5921Z" fill="black"/>
<path d="M42 0C45.3137 2.57702e-07 48 2.68629 48 6C48 6.70263 47.8772 7.37634 47.6553 8.00293L47.6699 8C47.6699 8 47.6587 8.02031 47.6367 8.05859C47.5387 8.32695 47.4218 8.58609 47.2881 8.83496C47.0559 9.45695 46.7554 10.4673 46.5 12C46.0815 14.5113 46 18 46 18L45.6201 10.7832C44.6137 11.5461 43.3602 12 42 12C38.6863 12 36 9.31371 36 6C36 4.46293 36.5788 3.0616 37.5293 2H30C30 2 33.4887 1.91854 36 1.5C37.5322 1.24463 38.542 0.943085 39.1641 0.710938C39.4129 0.577248 39.6721 0.460343 39.9404 0.362305C39.9789 0.340215 40 0.330078 40 0.330078L40.0029 0.34082C40.6277 0.120345 41.2998 0 42 0Z" fill="#E1E1E1"/>
<path d="M46 6C46 8.20914 44.2091 10 42 10C39.7909 10 38 8.20914 38 6C38 3.79086 39.7909 2 42 2C44.2091 2 46 3.79086 46 6Z" fill="#119037" class="indicator"/>
</svg>
`;
}

async function createMap(): Promise<OSMMap> {
    const liberty = new LayerGroup();

    // Загружаем стиль Mapbox в группу слоев
    await apply(liberty, '/static/dark.json');

    // Переопределяем метод получения класса ДО того, как карта начнет создавать DOM-элементы
    liberty.getLayers().forEach((layer) => {
        layer.getClassName = () => 'mapbox-contrast-layer';
    });

    return new OSMMap({
        target: 'map',
        layers: [
            liberty,
        ],
        view: new View({
            center: [3385010.432348291, 8338176.0138466135],
            zoom: 14.732147150226549,
            minZoom: 15,
        }),
    });
}

function goToLocation(geolocation: Geolocation, map: OSMMap) {
    const coordinates = geolocation.getPosition();
    if (coordinates) {
        map.getView().animate({
            center: coordinates,
            zoom: 16,
            duration: 1000,
        });
    } else {
        alert('Местоположение еще не определено. Убедитесь, что дали разрешение в браузере.');
    }
}

type MapProps = {
    isGeolocationTrackingEnabled: boolean;
    onLocationChanged: (geolocation: ObjectEvent) => unknown;
    userQuestsState: UserQuestState[] | null;
    onScenarioActivationRequired: ({scenarioId, activationCondition}: { scenarioId: string, activationCondition: string }) => unknown;
};

export default function Map({isGeolocationTrackingEnabled, onLocationChanged, userQuestsState, onScenarioActivationRequired}: MapProps) {
    const [map, setMap] = useState<OSMMap | null>(null);
    const [geolocator, setGeolocator] = useState<Geolocation | null>(null);

    useEffect(() => {
        (async () => setMap(await createMap()))();
    }, []);

    function trackLocationsActivation() {
        assert(map !== null);

        const position = geolocator?.getPosition() ?? null;
        if (position === null) {
            return;
        }

        let vectorLayer = map.getLayers().getArray().find(
            (layer) => layer.get('id') === 'quest-layer'
        ) as VectorLayer<VectorSource> | undefined;

        const source = vectorLayer?.getSource();

        if (source === null || source === undefined) {
            return;
        }

        for (const feature of source.getFeatures()) {
            if (feature.get("isActivationFeature") === true) {
                if (feature.getGeometry()?.intersectsCoordinate(position)) {
                    onScenarioActivationRequired({
                        scenarioId: feature.get('scenarioId') as string,
                        activationCondition: feature.get('activationCondition') as string
                    });
                }
            }
        }
    }

    useEffect(() => {
        if (map !== null) {
            setGeolocator(createLocation(map));
        }
    }, [map]);

    useEffect(() => {
        if (userQuestsState !== null && map !== null) {
            createQuestLocations(map, userQuestsState);
        }
    }, [map, userQuestsState]);

    useEffect(() => {
        if (geolocator !== null) {
            geolocator.setTracking(isGeolocationTrackingEnabled);
            geolocator.on("change:position", onLocationChanged);
            geolocator.on('change:accuracyGeometry', trackLocationsActivation);
            geolocator.on('change:position', trackLocationsActivation)
        }
    }, [geolocator, isGeolocationTrackingEnabled]);


    return <div id="map"></div>;
}